import { ref, computed, h } from 'vue'
import { defineStore } from 'pinia'
import { db } from '@/js/firebase'
import { collection, query, doc, setDoc, onSnapshot, deleteDoc, where, getDocs, getDoc, serverTimestamp, updateDoc, limit } from 'firebase/firestore'
import { useAuthStore } from '@/stores/storeAuth';
import { listMyManagedEntities } from '@/services/entities'
import { claimPreauthorizedAccount } from '@/services/auth'
import { resolvePostLoginRoute } from '@/services/authRedirect'
import moment from 'moment'

let getAthleteConnectionsSnapshot = null
let getShareSessionSnapshot = null
let getMySessionSnapshot = null
let getCoachesSnapshot = null
let getMyAthletesSnapshot = null

const delay = ms => new Promise(res => setTimeout(res, ms));

export const useFirebaseAppStore = defineStore('firebaseApp', {
    state: () => (
        {
            currentUser: null,
            availableCoachConnections: [],
            sessions: [],
            myAthletes: [],
            availableCoaches: [],
            sessionLoaded: false,
            isLoading: true,
            managedEntities: []
        }),
    getters: {
        // True if the current user has an active 'manager' membership on at
        // least one Team or Squad -- Director vs Coach is just a display
        // label (see backend's label_for_manager), not branched on here.
        isManager: (state) => state.managedEntities.length > 0,
        getAvailableConnections: (state) => {
            return state.availableCoachConnections.length > 0 ? "Got some" : "got none"
        },
        getCurrentUser: (state) => {
            return state.currentUser
        },
        getSharedSessions: (state) => {
            return state.firebaseApp
        },
        isUserSet: (state) => {
            if (state.currentUser == null) {
                return false
            }
            return true
        },
        isUser: (state) => {
            if (state.currentUser == null || state.currentUser.role == 'user') {
                return true
            } else {
                return false
            }
        },
        athleteHasCoachRequests: (state) => {
            return state.availableCoachConnections.length > 0
        },
        ahtleteHasCoach: (state) => {
            return state.availableCoachConnections.filter(c => c.state == 'accepted').length > 0
        },
        hasSharedSessions: (state) => {
            return state.sessions.length > 0
        },
        hasCoachSessions: (state) => {
            return state.sessions.length > 0
        },
        getSessionContent: (state) => {
            return (id) => {
                return state.sessions.filter(session => session.id === id)[0]
            }
        }
    },
    actions: {

        async init(user) {
            // Initialize the user
            const authStore = useAuthStore();

            console.log('-- INIT START -- user:', user.email);

            // Everything below is wrapped so a failure anywhere in this
            // chain (a Firestore read, a backend call, ...) can never
            // silently strand the user on /login: previously an uncaught
            // error here became an unhandled promise rejection (this
            // function is invoked fire-and-forget from storeAuth.js's
            // onAuthStateChanged listener) that skipped the router.push
            // below entirely, even though Firebase Auth itself had already
            // succeeded -- the "signed in but stuck on the sign-in screen"
            // bug. On failure we still fall through to the redirect below
            // using whatever profile/role state was resolved so far.
            try {
                // Fetch user profile to get roles
                await this.fetchUserProfile(user);

                console.log('-- INIT AFTER FETCH PROFILE -- currentUser is:', this.currentUser);

                // If the user has no profile, this is either a brand-new
                // self-service sign-up (no users/{uid} doc exists yet -- see
                // PageRegister.vue, which just calls authStore.register() and
                // otherwise leaves this same auth-state-driven flow to finish
                // the job, same as login) or a legacy shared-reports-only
                // visitor. Try claiming a pre-authorized placeholder profile
                // first: a Manager must have already pre-created this email via
                // My Team's "Add New Athlete" for this to succeed.
                if (!this.currentUser) {
                    console.log('-- INIT NO PROFILE -- attempting to claim a pre-authorized placeholder for:', user.email);
                    try {
                        await claimPreauthorizedAccount()
                        await this.fetchUserProfile(user)
                        console.log('-- INIT CLAIMED PRE-AUTHORIZED PROFILE -- currentUser is now:', this.currentUser);
                    } catch (error) {
                        console.log('-- INIT NOT PRE-AUTHORIZED -- falling back to shared-reports check:', error.message);
                    }
                }

                if (!this.currentUser) {
                    console.log('-- INIT CHECKING SHARED REPORTS -- for user:', user.email);
                    const hasSharedReports = await this.checkSharedReports(user.email)
                    if (!hasSharedReports) {
                        console.log('No shared reports found - redirecting to unauthorized page')
                        // Sign out the user and redirect to unauthorized page
                        await authStore.logout()
                        authStore.router.push('/unauthorized')
                        return
                    }
                } else {
                    console.log('-- INIT REGISTERED USER -- skipping shared reports check!');
                }

                // Fetch the caller's own managed entities (if any) so isManager
                // is known before the redirect decision below, and so Header.vue
                // can show the "My Team" nav item without a second fetch.
                //
                // Retried up to 3x with backoff: this call hits the FastAPI
                // backend on Cloud Run, which can cold-start slowly (see
                // aero-coach-backend's known cold-start/throttling behavior) --
                // without a retry, a single transient failure right after login
                // permanently misclassifies a real manager as a plain user for
                // the rest of the session (managedEntities has no other refresh
                // point), sending them to /my-profile instead of /my-team.
                if (this.currentUser && this.currentUser.role?.toLowerCase() !== 'admin') {
                    let lastError = null
                    this.managedEntities = []
                    for (let attempt = 0; attempt < 3; attempt++) {
                        try {
                            this.managedEntities = await listMyManagedEntities()
                            lastError = null
                            break
                        } catch (error) {
                            lastError = error
                            if (attempt < 2) await delay(400 * (attempt + 1))
                        }
                    }
                    if (lastError) {
                        console.error('Error loading managed entities after retries:', lastError)
                    }
                }

                // User has shared reports, redirect to results page (admin, manager, or shared reports)
                // But only if we are currently on landing/login/register/unauthorized paths
                const currentPath = authStore.router.currentRoute.value.path;
                if (currentPath === '/login' || currentPath === '/register' || currentPath === '/unauthorized') {
                    authStore.router.push(resolvePostLoginRoute(this))
                }
            } catch (error) {
                console.error('-- INIT FAILED -- unexpected error finishing sign-in setup:', error)
                authStore.errorsOccurred = true
                authStore.errorMessage = {
                    message: 'Something went wrong finishing sign-in. Please try again.',
                    code: 0
                }
            }

            // give a chance and debounce
            await delay(500)
            this.isLoading = false
        },
        async addAeroSeminar(seminarData) {
            try {
                const uniqueId = crypto.randomUUID();
                await setDoc(doc(db, "AeroSeminars", uniqueId), {
                    ...seminarData,
                    id: uniqueId,
                    createdAt: serverTimestamp()
                });
                return uniqueId;
            } catch (error) {
                console.error("Error adding seminar:", error);
                throw error;
            }
        },
        async updateAeroSeminar(id, seminarData) {
            try {
                const seminarRef = doc(db, "AeroSeminars", id);
                await updateDoc(seminarRef, {
                    ...seminarData,
                    updatedAt: serverTimestamp()
                });
            } catch (error) {
                console.error("Error updating seminar:", error);
                throw error;
            }
        },
        async deleteAeroSeminar(id) {
            try {
                await deleteDoc(doc(db, "AeroSeminars", id));
            } catch (error) {
                console.error("Error deleting seminar:", error);
                throw error;
            }
        },
        async updateRegistrationStatus(registrationId, seminarId, newStatus) {
            try {
                const regRef = doc(db, "seminar_registrations", registrationId);
                const regSnap = await getDoc(regRef);

                if (!regSnap.exists()) throw new Error("Registration not found");

                const oldStatus = regSnap.data().status;

                await updateDoc(regRef, {
                    status: newStatus,
                    updatedAt: serverTimestamp()
                });

                // Handle availability changes
                // If now 'registered' and wasn't before: DECREMENT (Take Spot)
                if (newStatus === 'registered' && oldStatus !== 'registered') {
                    await this.incrementAvailability(seminarId, -1);
                }
                // If was 'registered' and now isn't: INCREMENT (Free Spot)
                else if (oldStatus === 'registered' && newStatus !== 'registered') {
                    await this.incrementAvailability(seminarId, 1);
                }
            } catch (error) {
                console.error("Error updating registration status:", error);
                throw error;
            }
        },
        async incrementAvailability(seminarId, amount) {
            try {
                const seminarRef = doc(db, "AeroSeminars", seminarId);
                const seminarSnap = await getDoc(seminarRef);
                if (seminarSnap.exists()) {
                    const currentVal = seminarSnap.data().availability || 0;
                    await updateDoc(seminarRef, {
                        availability: currentVal + amount
                    });
                }
            } catch (error) {
                console.error("Error adjusting availability:", error);
            }
        },
        isValidCoachEmail(email) {
            return availableCoaches.filter(c => c.coachEmail == email).length > 0
        },
        async fetchUserProfile(user) {
            console.log('Fetch profile for user=', user)
            try {
                const snap = await getDoc(doc(db, 'users', user.uid))

                if (snap.exists()) {
                    console.log('-- FETCH PROFILE -- user found:', snap.data())
                    this.currentUser = snap.data()
                }
                else {
                    console.log("-- FETCH PROFILE -- No such user - user is not authorized (" + user.email + ")")
                    this.currentUser = null
                }
            } catch (error) {
                // Treat a failed read the same as "no profile found" rather
                // than letting it escape as an uncaught rejection -- init()
                // still needs to reach its redirect below.
                console.error('-- FETCH PROFILE -- failed to read profile, treating as no profile:', error)
                this.currentUser = null
            }
        },

        async checkSharedReports(userEmail) {
            try {
                const q = query(
                    collection(db, 'SharedSummaryReports'),
                    where('sharedWithEmail', '==', userEmail.toLowerCase())
                )
                const querySnapshot = await getDocs(q)
                return !querySnapshot.empty
            } catch (error) {
                console.error('Error checking shared reports:', error)
                return false
            }
        },

        async fetchAthleteConnections() {
            // Clear any active listners
            if (getAthleteConnectionsSnapshot) {
                getAthleteConnectionsSnapshot()
            }

            // Start listening to the shared sessions
            if (!this.currentUser?.email) {
                console.log('No current user email for athlete connections')
                return
            }
            const q = query(collection(db, 'coach-athletes'), where("athleteEmail", "==", this.currentUser.email))
            getAthleteConnectionsSnapshot = onSnapshot(q, (querySnapshot) => {
                let connections = []
                querySnapshot.forEach((doc) => {
                    //console.log(doc.id, " -athlete connections - => ", doc.data());
                    let connection = {
                        id: doc.data().id,
                        coachEmail: doc.data().coachEmail,
                        athleteEmail: doc.data().athleteEmail,
                        athleteMessage: doc.data().athleteMessage,
                        state: doc.data().state,
                        requestDate: doc.data().requestDate,
                        lastUpdated: doc.data().lastUpdated,
                    }
                    connections.push(connection)
                    //console.log("Pending connection=", connections.length)
                })
                //console.log("Pending connection=", connections.length)
                this.availableCoachConnections = connections
                //console.log("Pending connection=", this.availableCoachConnections.length)
            })
        },
        async fetchSharedSessions() {
            //console.log('UserId=', userId)
            if (this.currentUser?.id) {
                const q = query(collection(db, 'coach-athlete-share'), where("athleteId", "==", this.currentUser.id))

                // Clear any active listners
                if (getShareSessionSnapshot) {
                    getShareSessionSnapshot()
                }

                // Start listening to the shared sessions
                getShareSessionSnapshot = onSnapshot(q, async (querySnapshot) => {
                    let sss = []
                    querySnapshot.forEach((doc) => {
                        console.log(doc.id, " shared with me => ", doc.data());
                        let ss = {
                            id: doc.data().id,
                            sessionId: doc.data().sessionId
                        }
                        sss.push(ss)
                        //console.log(ss)
                    })
                    this.sessions = []
                    sss.forEach(async (element) => {
                        let sessionDetails = await this.fetchSessionById(element.sessionId)
                        if (sessionDetails != null) {
                            //var foundIndex = this.sessions.findIndex(x => x.id == element.id)
                            //if (foundIndex >= 0){
                            //    this.sessions[foundIndex] = sessionDetails
                            //}else{
                            this.sessions.push(sessionDetails)
                            //}
                        }

                    })
                })
            }

        },
        async fetchSessionById(sessionId) {
            const snap = await getDoc(doc(db, 'sessions', sessionId))

            if (snap.exists()) {
                //console.log(snap.data())
                return snap.data()
            } else {
                console.log("no session for id=", sessionId)
                return null
            }
        },
        async fetchCoaches() {
            //console.log('UserId=', userId)
            if (this.currentUser != null) {
                const q = query(collection(db, 'users'), where('role', '==', 'coach'))

                // Clear any active listners
                if (getCoachesSnapshot) {
                    getCoachesSnapshot()
                }

                // Start listening to the coaches
                getCoachesSnapshot = onSnapshot(q, (querySnapshot) => {
                    let coaches = []
                    querySnapshot.forEach((doc) => {
                        //console.log(doc.id, " => ", doc.data());
                        let coach = {
                            id: doc.data().id,
                            coachEmail: doc.data().email,
                            coachName: doc.data().fullname
                        }
                        coaches.push(coach)
                        //console.log(ss)
                    })
                    this.availableCoaches = coaches
                })
            }
        },
        async fetchMySessions() {
            if (this.currentUser?.id) {
                // Must be the LOWERCASED auth-token email. sessions.userId is
                // stored lowercase, and firestore.rules only permits a `list`
                // whose filter equals request.auth.token.email.lower() — any
                // other value is denied outright, not merely empty. Prefer the
                // token over currentUser.id, since the token is what the rule
                // actually compares against.
                const authStore = useAuthStore()
                const ownerKey = String(authStore.user?.email || this.currentUser.id).toLowerCase()
                console.log('fetchMySessions UserId=', ownerKey)
                const q = query(collection(db, 'sessions'), where("userId", "==", ownerKey))

                // Clear any active listners
                if (getMySessionSnapshot) {
                    getMySessionSnapshot()
                }

                // Start listening to the my sessions
                getMySessionSnapshot = onSnapshot(q, (querySnapshot) => {
                    let sss = []
                    querySnapshot.forEach((doc) => {
                        let ss = doc.data()
                        sss.push(ss)
                        console.log('Got a session', ss)
                    })
                    this.sessions = sss.sort(this.compare)
                })
            }
        },
        async fetchMyAthletes() {
            //console.log('UserId=', userId)
            if (this.currentUser?.email) {
                const q = query(collection(db, 'coach-athletes'), where("coachEmail", "==", this.currentUser.email))

                // Clear any active listners
                if (getMyAthletesSnapshot) {
                    getMyAthletesSnapshot()
                }

                // Start listening to the my athletes
                getMyAthletesSnapshot = onSnapshot(q, (querySnapshot) => {
                    let sss = []
                    querySnapshot.forEach((doc) => {
                        console.log(doc.id, " Athlete => ", doc.data());
                        sss.push(doc.data())
                        //console.log(ss)
                    })
                    this.myAthletes = sss
                })
            }
        },
        async requestCoachConnection(coachEmail, message) {
            let uniqueId = crypto.randomUUID()
            let request = {
                id: uniqueId,
                coachEmail: coachEmail,
                athleteEmail: this.currentUser.email,
                athleteMessage: message,
                state: "requested",
                requestDate: new Date(),
                lastUpdated: new Date(),
            }
            await setDoc(doc(db, "coach-athletes", uniqueId), request)
        },
        async deleteSharedSession(idToDelete) {
            await deleteDoc(doc(db, "firebaseApp", idToDelete))
        },
        clearCaches() {
            if (getAthleteConnectionsSnapshot) {
                getAthleteConnectionsSnapshot()
                getAthleteConnectionsSnapshot = null
            }
            if (getShareSessionSnapshot) {
                getShareSessionSnapshot()
                getShareSessionSnapshot = null
            }
            if (getMySessionSnapshot) {
                getMySessionSnapshot()
                getMySessionSnapshot = null
            }
            if (getCoachesSnapshot) {
                getCoachesSnapshot()
                getCoachesSnapshot = null
            }
            if (getMyAthletesSnapshot) {
                getMyAthletesSnapshot()
                getMyAthletesSnapshot = null
            }
            this.currentUser = null
            this.availableCoachConnections = []
            this.firebaseApp = []
            this.myAthletes = []
        },
        async getSessionDetail() {
            this.sessionLoaded = false

            this.sessionLoaded = true
        },
        async createVirtualSession(sessionName) {
            try {
                const uniqueId = crypto.randomUUID();
                const authStore = useAuthStore();
                // Lowercased to match how sessions.userId is stored elsewhere;
                // otherwise fetchMySessions (an exact-match query) misses it.
                const ownerKey = String(authStore.user?.email || this.currentUser?.id || '').toLowerCase();
                const newSession = {
                    id: uniqueId,
                    userId: ownerKey || null,
                    name: sessionName || 'Virtual CFD Session',
                    timestamp: serverTimestamp(),
                    isVirtual: true,
                    totalMass: 75,
                    groupedTests: [],
                    tests: []
                };
                await setDoc(doc(db, "sessions", uniqueId), newSession);
                return uniqueId;
            } catch (error) {
                console.error("Error creating virtual session:", error);
                throw error;
            }
        },
        // Persists edited aero parameters and marks the session as pending
        // re-analysis. paramsDoc must come from formToSessionDoc() and must
        // already have been validated by the caller.
        async updateSessionParams(sessionId, paramsDoc) {
            try {
                await updateDoc(doc(db, "sessions", sessionId), {
                    ...paramsDoc,
                    paramsUpdatedAt: serverTimestamp(),
                    // Marks the document as diverged from what the iOS app
                    // holds. Nothing syncs back, so the device keeps the CdA it
                    // originally computed and these values only exist here.
                    //
                    // Self-clearing: an iOS re-upload writes the session with
                    // setData and no merge (FirebaseViewModel+Sessions.swift),
                    // replacing the whole document, so these fields disappear
                    // exactly when the device's copy becomes authoritative again.
                    webEditedAt: serverTimestamp(),
                    webEditedBy: this.currentUser?.fullname || this.currentUser?.id || null,
                    analysis_status: "processing",
                    error_message: null
                });
            } catch (error) {
                console.error("Error updating session params:", error);
                throw error;
            }
        },
        // Live subscription to a single session doc. Returns an unsubscribe fn;
        // the caller owns cleanup.
        subscribeToSession(sessionId, onChange) {
            return onSnapshot(
                doc(db, "sessions", sessionId),
                (snap) => onChange(snap.exists() ? { id: sessionId, ...snap.data() } : null),
                (err) => console.error("Session subscription error:", err)
            );
        },
        // Exact-match lookup only — no enumeration. users is keyed by uid, not
        // email, so this is a `where` query rather than a doc-by-id lookup.
        async lookupUserByEmail(email) {
            const key = String(email).trim().toLowerCase()
            if (!key) return null
            const q = query(collection(db, 'users'), where('email', '==', key), limit(1))
            const snapshot = await getDocs(q)
            return snapshot.empty ? null : snapshot.docs[0].data()
        },
        // Shares the current owner has granted on this session. Firestore
        // rules only allow listing session-shares where the caller is
        // fromId or toId, so this is scoped to shares the caller created.
        async fetchSessionShares(sessionId) {
            const me = this.currentUser?.id
            if (!me) return []

            const q = query(
                collection(db, 'session-shares'),
                where('sessionId', '==', sessionId),
                where('fromId', '==', me)
            )
            const snap = await getDocs(q)

            const shares = await Promise.all(snap.docs.map(async (shareDoc) => {
                const data = shareDoc.data()
                const recipient = await this.lookupUserByEmail(data.toId)
                return {
                    id: shareDoc.id,
                    toId: data.toId,
                    recipientName: recipient?.fullname || recipient?.name || null
                }
            }))

            return shares
        },
        // Verifies the recipient is a real LeGizmo user before writing the
        // share record — no share is created for an unverified email.
        async shareSessionWithUser(sessionId, toEmail) {
            const me = this.currentUser?.id
            if (!me) throw new Error('Not signed in')

            const toId = String(toEmail).trim().toLowerCase()
            if (!toId) throw new Error('Enter an email address')
            if (toId === me) throw new Error('You already have access to your own session')

            const recipient = await this.lookupUserByEmail(toId)
            if (!recipient) throw new Error('No LeGizmo user found with that email')

            const existing = await getDocs(query(
                collection(db, 'session-shares'),
                where('sessionId', '==', sessionId),
                where('fromId', '==', me),
                where('toId', '==', toId)
            ))
            if (!existing.empty) throw new Error('Already shared with this user')

            const shareRef = doc(collection(db, 'session-shares'))
            await setDoc(shareRef, {
                fromId: me,
                toId,
                sessionId,
                createdAt: serverTimestamp()
            })

            return { id: shareRef.id, toId, recipientName: recipient.fullname || recipient.name || null }
        },
        async revokeSessionShare(shareId) {
            await deleteDoc(doc(db, 'session-shares', shareId))
        },
        // Who shared this session with the current viewer, if anyone. Used to
        // show "shared by X" on the session detail page. Returns null for the
        // owner's own sessions and for admin-viewed sessions with no share record.
        async fetchIncomingShare(sessionId) {
            const me = this.currentUser?.id
            if (!me) return null

            const q = query(
                collection(db, 'session-shares'),
                where('sessionId', '==', sessionId),
                where('toId', '==', me)
            )
            const snap = await getDocs(q)
            if (snap.empty) return null

            const shareData = snap.docs[0].data()
            const sharer = await this.lookupUserByEmail(shareData.fromId)
            return {
                fromId: shareData.fromId,
                fromName: sharer?.fullname || sharer?.name || null
            }
        },
        compare(a, b) {
            const aln = a.collection.toLowerCase();
            const bln = b.collection.toLowerCase();

            if (aln < bln) {
                return -1;
            }

            if (aln > bln) {
                return 1;
            }

            return 0;
        }
    }
})
